import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  X,
  MoreVertical,
  Eye,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
  Loader2,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ClipboardList,
  AlertTriangle,
  History,
  Briefcase,
  Users,
  LayoutDashboard
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Pagination from '../../components/Common/Pagination/Pagination';
import TaskDrawer from '../../components/Tasks/TaskDrawer';
import Footer from '../../components/Common/Footer/Footer';
import Swal from 'sweetalert2';
import { useLoading } from '../../context/LoadingContext';
import NoData from '../../components/Common/NoData/NoData';
import styles from './Tasks.module.css';

const Tasks = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const { showLoader, hideLoader, loading } = useLoading();

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = currentUser.permissions || [];
  const isAdmin = (currentUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create tasks');
  const canEdit = isAdmin || userPermissions.includes('edit tasks');
  const canDelete = isAdmin || userPermissions.includes('delete tasks');
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [projects, setProjects] = useState([]);

  const fetchInitialData = useCallback(async () => {
    try {
      const response = await api.get('/projects?per_page=1000');
      if (response.data.success) {
        setProjects(response.data.data.data.map(p => ({ value: p.id, label: p.project_name })));
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  // Drawer Management
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const fetchTasks = useCallback(async (page = 1) => {
    try {
      const params = {
        page,
        per_page: pagination.per_page,
        search: searchTerm,
        status: statusFilter?.value,
        priority: priorityFilter?.value,
        project_id: projectFilter?.value
      };

      const response = await api.get('/tasks', { params });
      if (response.data.success) {
        const { data, ...meta } = response.data.data;
        setTasks(data);
        setPagination(meta);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (error.response?.status === 404) {
        setTasks([]);
        setPagination(prev => ({ ...prev, total: 0, last_page: 1, current_page: 1 }));
      } else {
        toast.error('Failed to load tasks');
      }
    }
  }, [searchTerm, statusFilter, priorityFilter, projectFilter, pagination.per_page]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTasks(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, statusFilter, priorityFilter, projectFilter]);

  const handleDelete = async (task) => {
    if (!task) return;

    Swal.fire({
      html: `
        <style>
          .swal-popup-rounded {
            border-radius: 20px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          .swal-confirm-btn-primary {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 10px !important;
            padding: 12px 24px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2) !important;
          }
          .swal-confirm-btn-primary:hover {
            background-color: #b91c1c !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 6px 16px rgba(220, 38, 38, 0.3) !important;
          }
          .swal-cancel-btn-secondary {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 10px !important;
            padding: 12px 24px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            margin-right: 12px !important;
          }
          .swal-cancel-btn-secondary:hover {
            background-color: #f8fafc !important;
            border-color: #cbd5e1 !important;
          }
          .swal-actions-wrapper {
            display: flex !important;
            gap: 16px !important;
            margin-top: 24px !important;
            width: 100% !important;
          }
          .custom-icon-wrapper-swal {
            width: 64px;
            height: 64px;
            background-color: #fef2f2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper-swal">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Task?</h2>
        <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
          Are you sure you want to delete <strong>${task.title}</strong>?
        </p>
        <p style="margin: 0; color: #94a3b8; font-size: 13px;">
          This action will permanently remove this task and all related discussions.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete Task',
      cancelButtonText: 'No, Keep It',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#f1f5f9',
      customClass: {
        confirmButton: 'swal-confirm-btn-primary',
        cancelButton: 'swal-cancel-btn-secondary',
        actions: 'swal-actions-wrapper',
        popup: 'swal-popup-rounded'
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/tasks/${task.id}`);
          if (response.data.success) {
            toast.success('Task deleted successfully');
            fetchTasks(pagination.current_page);
          }
        } catch (error) {
          console.error('Error deleting task:', error);
          toast.error('Failed to delete task');
        }
      }
    });
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return styles['priority-high'];
      case 'medium': return styles['priority-medium'];
      case 'low': return styles['priority-low'];
      default: return '';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return styles['status-open'];
      case 'in_progress': return styles['status-in_progress'];
      case 'completed': return styles['status-completed'];
      case 'overdue': return styles['status-overdue'];
      default: return '';
    }
  };

  const metricCards = [
    { label: 'Total Tasks', value: pagination.total, icon: <ClipboardList size={24} />, color: 'blue' },
    { label: 'Open', value: tasks.filter(t => t.status === 'open').length, icon: <Clock size={24} />, color: 'orange' },
    { label: 'In Progress', value: tasks.filter(t => t.status === 'in_progress').length, icon: <History size={24} />, color: 'blue' },
    { label: 'Completed', value: tasks.filter(t => t.status === 'completed').length, icon: <CheckCircle2 size={24} />, color: 'green' }
  ];

  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const toggleActionMenu = (e, index, task) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setOpenMenuIndex(index);
      setSelectedTask(task);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuIndex(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={styles['tasks-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title']}>
              <h1>Task Management</h1>
              <p>Track, assign, and manage project tasks across your organization.</p>
            </div>
            {canCreate && (
              <button className={styles['btn-primary']} onClick={() => { setEditingTask(null); setIsDrawerOpen(true); }}>
                <Plus size={20} />
                <span>New Task</span>
              </button>
            )}
          </div>

          {/* Summary Toggle for Mobile */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {pagination.total} Tasks · {tasks.filter(t => t.status === 'open').length} Open
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['metrics-grid']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metricCards.map((card, idx) => (
                <div key={idx} className={`${styles['metric-card']} ${styles[card.color]}`}>
                  <div className={styles['metric-icon-box']}>
                    {card.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <label>{card.label}</label>
                    <div className={styles['metric-value']}>{card.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search tasks by title..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(opt) => setPagination(prev => ({ ...prev, per_page: opt.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={18} />
                <span>Filters</span>
                {(statusFilter || priorityFilter || projectFilter) && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Tasks</h3>
                  <p>Refine your view by applying specific criteria.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'overdue', label: 'Overdue' }
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder="All Status"
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Priority</label>
                  <CustomSelect
                    options={[
                      { value: 'high', label: 'High Priority' },
                      { value: 'medium', label: 'Medium Priority' },
                      { value: 'low', label: 'Low Priority' }
                    ]}
                    value={priorityFilter}
                    onChange={setPriorityFilter}
                    placeholder="All Priority"
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    options={projects}
                    value={projectFilter}
                    onChange={setProjectFilter}
                    placeholder="All Projects"
                    isClearable
                  />
                </div>
              </div>
            </div>
          )}

          <div className={styles['custom-table-container']}>
            <div className={styles['table-responsive']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>TASK DETAILS</th>
                    <th>PRIORITY</th>
                    <th>DUE DATE</th>
                    <th>STATUS</th>
                    <th>ASSIGNED TO</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length > 0 ? (
                    tasks.map((task) => (
                      <tr key={task.id}>
                        <td className={styles['task-title-cell']}>
                          <span className={styles['task-title']} onClick={() => navigate(`/tasks/view/${task.id}`)}>
                            {task.title}
                          </span>
                          <span className={styles['project-tag']}>
                            {task.project?.project_name || 'General Task'}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles['badge']} ${getPriorityClass(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td>
                          <div className={styles['due-date']}>
                            {task.due_date ? new Date(task.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No Deadline'}
                          </div>
                        </td>
                        <td>
                          <span className={`${styles['badge']} ${getStatusClass(task.status)}`}>
                            <div className={styles['dot']}></div>
                            {task.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <div className={styles['assigned-users']}>
                            {task.assigned_employees?.length > 0 ? (
                              <span>{task.assigned_employees.length} Members</span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>Unassigned</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={styles['action-btns']}>
                            <button 
                              className={`${styles['icon-btn']} ${openMenuIndex === `desktop-${task.id}` ? styles['active'] : ''}`} 
                              onClick={(e) => toggleActionMenu(e, `desktop-${task.id}`, task)}
                            >
                              <MoreVertical size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    !loading && (
                      <NoData message="No tasks found matching your criteria." isTable={true} />
                    )
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['task-cards']}>
              {tasks.length > 0 ? (
                tasks.map((task) => (
                  <div key={task.id} className={styles['task-card']} onClick={() => navigate(`/tasks/view/${task.id}`)}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>#{task.id}</span>
                        <span className={styles['card-title']}>{task.title}</span>
                      </div>
                      <div className={styles['mobile-action-menu']}>
                        <button 
                          className={`${styles['icon-btn']} ${openMenuIndex === `mobile-${task.id}` ? styles['active'] : ''}`} 
                          onClick={(e) => toggleActionMenu(e, `mobile-${task.id}`, task)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>

                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Project</span>
                        <span className={styles['detail-value']}>{task.project?.project_name || 'General Task'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Priority</span>
                        <span className={`${styles['status-pill']} ${getPriorityClass(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Due Date</span>
                        <span className={styles['detail-value']}>
                          {task.due_date ? new Date(task.due_date).toLocaleDateString('en-GB') : 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Users size={14} />
                        <span>{task.assigned_employees?.length || 0} Assigned</span>
                      </div>
                      <span className={`${styles['badge']} ${getStatusClass(task.status)}`}>
                        <div className={styles['dot']}></div>
                        {task.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                !loading && <NoData message="No tasks found matching your criteria." isTable={false} />
              )}
            </div>

            {pagination.last_page > 1 && (
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                onPageChange={fetchTasks}
              />
            )}
          </div>

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={styles['action-dropdown']}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className={styles['dropdown-item']} 
                onClick={() => { setOpenMenuIndex(null); navigate(`/tasks/view/${selectedTask?.id}`); }}
              >
                <Eye size={16} /> <span>View Details</span>
              </button>
              {(currentUser?.id === selectedTask?.created_by || canEdit) && (
                <>
                  <button 
                    className={styles['dropdown-item']} 
                    onClick={() => { setOpenMenuIndex(null); setEditingTask(selectedTask); setIsDrawerOpen(true); }}
                  >
                    <Edit2 size={16} /> <span>Edit Task</span>
                  </button>
                  <div className={styles['dropdown-divider']}></div>
                  {(currentUser?.id === selectedTask?.created_by || canDelete) && (
                    <button 
                      className={`${styles['dropdown-item']} ${styles['delete-item']}`} 
                      onClick={() => { setOpenMenuIndex(null); handleDelete(selectedTask); }}
                    >
                      <Trash2 size={16} /> <span>Delete Task</span>
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => fetchTasks(page)}
            />
          </div>

          <TaskDrawer 
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            taskData={editingTask}
            onSave={() => fetchTasks(pagination.current_page)}
          />

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Tasks;
